import React, { useEffect, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { Ingredient, IngredientAlias, IngredientPrice, IngredientType, UnitType } from "../../../types/mes";
import IngredientAliasPicker from "../../../components/MES/IngredientAliasPicker";
import { ToastContainer, toast } from "react-toastify";

const emptyIngredient: Partial<Ingredient> = {
  IngredientName: "",
  StandardCode: "",
  IngredientType: null,
  UnitType: null,
  IsPerishable: false,
  IsActive: true,
};

/**
 * Ingredients Page - View and manage ingredients
 */
const Ingredients = () => {
  savePathToLocalStorage();

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [ingredientTypes, setIngredientTypes] = useState<IngredientType[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedIngredient, setSelectedIngredient] = useState<Ingredient | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newIngredient, setNewIngredient] = useState<Partial<Ingredient>>({ ...emptyIngredient });
  const [aliases, setAliases] = useState<IngredientAlias[]>([]);
  const [newAliasIds, setNewAliasIds] = useState<number[]>([]);
  const [detailAliasIds, setDetailAliasIds] = useState<number[]>([]);

  useEffect(() => {
    loadIngredients();
    loadLookups();
  }, []);

  const loadIngredients = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getIngredients();
      setIngredients(data);
    } catch (err: any) {
      toast.error("Failed to load ingredients: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadLookups = async () => {
    try {
      const [types, units, aliasList] = await Promise.all([
        mesApi.getIngredientTypes(),
        mesApi.getUnitTypes(),
        mesApi.getIngredientAliases(),
      ]);
      setIngredientTypes(types);
      setUnitTypes(units);
      setAliases(aliasList);
    } catch (err: any) {
      console.error("Failed to load lookups:", err);
    }
  };

  const handleNewFieldChange = (field: keyof Ingredient, value: any) => {
    setNewIngredient((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateIngredient = async () => {
    if (!newIngredient.IngredientName?.trim()) {
      toast.error("Naziv sastojka je obavezan");
      return;
    }
    setSaving(true);
    try {
      await mesApi.createIngredient({ ...newIngredient, AliasIDs: newAliasIds });
      toast.success("Sastojak uspjesno dodan");
      setCreateModalOpen(false);
      setNewIngredient({ ...emptyIngredient });
      setNewAliasIds([]);
      loadIngredients();
      loadLookups();
    } catch (err: any) {
      toast.error("Greska pri dodavanju sastojka: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openDetails = (ing: Ingredient) => {
    setSelectedIngredient(ing);
    setDetailAliasIds((ing.Aliases ?? []).map((a) => a.ID));
  };

  const handleSaveDetailAliases = async () => {
    if (!selectedIngredient) return;
    setSaving(true);
    try {
      const updated = await mesApi.setIngredientAliases(selectedIngredient.ID, detailAliasIds);
      toast.success("Ritam artikli spremljeni");
      setSelectedIngredient(updated);
      loadIngredients();
      loadLookups();
    } catch (err: any) {
      toast.error("Greska pri spremanju artikala: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (value?: number | string) => {
    if (value === undefined || value === null) return "-";
    const num = typeof value === "string" ? parseFloat(value) : value;
    return num.toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " EUR/kg";
  };

  const getCurrentPrice = (ingredient: Ingredient): number | undefined => {
    const now = new Date();
    const prices = ingredient.IngredientPrice;
    if (!prices || prices.length === 0) return undefined;

    const activePrice = prices.find((p) => {
      const from = new Date(p.ValidFrom || "");
      const to = p.ValidTo ? new Date(p.ValidTo) : null;
      return from <= now && (!to || to >= now);
    });
    return activePrice?.PricePerKg;
  };

  const getIngredientId = (ing: Ingredient) => ing.ID;

  return (
    <div className="mes-page ingredients-page">
      <Header title="MES - Sastojci" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          {/* Toolbar */}
          <div className="mes-section filter-section" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
            <button
              className="btn-submit"
              onClick={() => {
                setNewIngredient({ ...emptyIngredient });
                setNewAliasIds([]);
                setCreateModalOpen(true);
              }}
            >
              + Dodaj sastojak
            </button>
          </div>

          {/* Ingredients Table */}
          {loading ? (
            <div className="mes-section loading-state">Loading...</div>
          ) : ingredients.length === 0 ? (
            <div className="mes-section empty-state">
              <p>Nema sastojaka u sustavu.</p>
            </div>
          ) : (
            <div className="mes-section">
              <h3>Sastojci</h3>
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>Naziv</th>
                    <th>Tip</th>
                    <th>Sifra</th>
                    <th>Jedinica</th>
                    <th>Ritam artikli</th>
                    <th>Trenutna cijena</th>
                    <th>Status</th>
                    <th>Akcije</th>
                  </tr>
                </thead>
                <tbody>
                  {ingredients.map((ing) => {
                    const currentPrice = getCurrentPrice(ing);
                    return (
                      <tr key={getIngredientId(ing)}>
                        <td style={{ fontWeight: 500 }}>{ing.IngredientName}</td>
                        <td>{ing.IngredientTypeName || ing.IngredientType_IngredientToIngredientType?.Name || "—"}</td>
                        <td style={{ fontFamily: "monospace" }}>{ing.StandardCode || "-"}</td>
                        <td>{ing.Unit || "kg"}</td>
                        <td>
                          {ing.Aliases && ing.Aliases.length > 0 ? (
                            ing.Aliases.map((a) => a.AliasName).join(", ")
                          ) : (
                            <span style={{ color: "#ffc107" }}>Nije povezano</span>
                          )}
                        </td>
                        <td>
                          {currentPrice !== undefined ? (
                            <span style={{ color: "#4b9987", fontWeight: 500 }}>
                              {formatCurrency(currentPrice)}
                            </span>
                          ) : (
                            <span style={{ color: "#ffc107" }}>Nema cijene</span>
                          )}
                        </td>
                        <td>
                          <span className={`status-pill status-${ing.IsActive ? "active" : "inactive"}`}>
                            <span className="status-dot"></span>
                            {ing.IsActive ? "Aktivan" : "Neaktivan"}
                          </span>
                        </td>
                        <td>
                          <button className="btn-sm btn-secondary" onClick={() => openDetails(ing)}>
                            Detalji
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Ingredient Details Modal */}
          {selectedIngredient && (
            <div className="mes-modal-overlay" onClick={() => setSelectedIngredient(null)}>
              <div className="mes-modal mes-modal-lg" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <div className="modal-title-group">
                    <div className="modal-icon">🧂</div>
                    <div className="modal-title-text">
                      <h3>{selectedIngredient.IngredientName.toUpperCase()}</h3>
                      <p className="modal-subtitle">Detalji sastojka</p>
                    </div>
                  </div>
                  <button className="close-btn" onClick={() => setSelectedIngredient(null)}>
                    ✕
                  </button>
                </div>
                <div className="modal-body">
                  <div className="ingredient-details-grid">
                    <div className="detail-item">
                      <span className="detail-label">Sifra</span>
                      <span className="detail-value">{selectedIngredient.StandardCode || "-"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Jedinica</span>
                      <span className="detail-value">{selectedIngredient.Unit || "kg"}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`status-pill status-${selectedIngredient.IsActive ? "active" : "inactive"}`}>
                        <span className="status-dot"></span>
                        {selectedIngredient.IsActive ? "Aktivan" : "Neaktivan"}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Trenutna cijena</span>
                      <span className="detail-value price-value">
                        {formatCurrency(getCurrentPrice(selectedIngredient))}
                      </span>
                    </div>
                  </div>

                  <h4 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>Ritam artikli</h4>
                  <div className="form-group">
                    <IngredientAliasPicker
                      aliases={aliases}
                      value={detailAliasIds}
                      onChange={setDetailAliasIds}
                      disabled={saving}
                    />
                    <span className="form-hint">
                      Isti Ritam artikl moze stajati na vise sirovina.
                    </span>
                  </div>
                  <button className="btn-sm btn-primary" onClick={handleSaveDetailAliases} disabled={saving}>
                    {saving ? "Spremanje..." : "Spremi artikle"}
                  </button>

                  <h4 style={{ marginTop: "1.5rem", marginBottom: "1rem" }}>Povijest cijena</h4>
                  {selectedIngredient.IngredientPrice && selectedIngredient.IngredientPrice.length > 0 ? (
                    <div className="mes-table-wrap">
                      <table className="mes-table">
                      <thead>
                        <tr>
                          <th>Od datuma</th>
                          <th>Do datuma</th>
                          <th>Cijena</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedIngredient.IngredientPrice
                          .sort((a, b) => new Date(b.ValidFrom || "").getTime() - new Date(a.ValidFrom || "").getTime())
                          .map((price) => (
                            <tr key={price.ID}>
                              <td>{new Date(price.ValidFrom || "").toLocaleDateString("hr-HR")}</td>
                              <td>{price.ValidTo ? new Date(price.ValidTo).toLocaleDateString("hr-HR") : "Aktivno"}</td>
                              <td className="price-value">{formatCurrency(price.PricePerKg)}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                    </div>
                  ) : (
                    <div className="empty-state" style={{ padding: "1rem" }}>
                      <p>Nema povijesti cijena.</p>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={() => setSelectedIngredient(null)}>
                    Zatvori
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Ingredient Modal */}
      {createModalOpen && (
        <div className="mes-modal-overlay" onClick={() => setCreateModalOpen(false)}>
          <div className="mes-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title-group">
                <div className="modal-icon">🧂</div>
                <div className="modal-title-text">
                  <h3>NOVI SASTOJAK</h3>
                  <p className="modal-subtitle">Dodavanje nove sirovine</p>
                </div>
              </div>
              <button className="close-btn" onClick={() => setCreateModalOpen(false)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Naziv sastojka *</label>
                <input
                  type="text"
                  placeholder="Naziv sastojka"
                  value={newIngredient.IngredientName || ""}
                  onChange={(e) => handleNewFieldChange("IngredientName", e.target.value)}
                />
              </div>
              <div className="form-grid-2">
                <div className="form-group">
                  <label>Tip sastojka</label>
                  <select
                    value={newIngredient.IngredientType ? String(newIngredient.IngredientType) : ""}
                    onChange={(e) =>
                      handleNewFieldChange("IngredientType", e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Odaberi tip</option>
                    {ingredientTypes.map((t) => (
                      <option key={t.ID} value={t.ID}>{t.Name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Jedinica mjere</label>
                  <select
                    value={newIngredient.UnitType ? String(newIngredient.UnitType) : ""}
                    onChange={(e) =>
                      handleNewFieldChange("UnitType", e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Odaberi jedinicu</option>
                    {unitTypes.map((u) => (
                      <option key={u.ID} value={u.ID}>{u.Code || u.Name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Sifra</label>
                <input
                  type="text"
                  placeholder="Sifra sastojka"
                  value={newIngredient.StandardCode || ""}
                  onChange={(e) => handleNewFieldChange("StandardCode", e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Ritam artikli</label>
                <IngredientAliasPicker
                  aliases={aliases}
                  value={newAliasIds}
                  onChange={setNewAliasIds}
                  disabled={saving}
                />
                <span className="form-hint">
                  Artikl iz Ritma koji se knjizi na ovu sirovinu. Moze ih biti vise, a isti
                  artikl smije stajati i na drugoj sirovini.
                </span>
              </div>
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={newIngredient.IsPerishable ?? false}
                    onChange={(e) => handleNewFieldChange("IsPerishable", e.target.checked)}
                  />
                  <label>Kvarljiva roba</label>
                </div>
              </div>
              <div className="form-group">
                <div className="checkbox-group">
                  <input
                    type="checkbox"
                    checked={newIngredient.IsActive ?? true}
                    onChange={(e) => handleNewFieldChange("IsActive", e.target.checked)}
                  />
                  <label>Aktivan sastojak</label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setCreateModalOpen(false)}>
                Odustani
              </button>
              <button className="btn-submit" onClick={handleCreateIngredient} disabled={saving}>
                {saving ? "Spremanje..." : "Dodaj"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default Ingredients;
